import { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { GenerationService, GenerationConfig, GenerationHandlers, Field } from '../services/generationService';
import { USERFEEDBACKACTIONS } from '../pages/EditProposal/reducers/userFeedbackReducer';
import { useProposal } from '@/pages/EditProposal/context/ProposalContext';

export function useGeneration(handlers: GenerationHandlers) {
    const { getAccessTokenSilently } = useAuth0();
    const { userFeedback, dispatchUserFeedback } = useProposal();
    const { loadingScope, loadingAssumptions, loadingLawyers, loadingExperiences } = userFeedback;

    // Add useEffect 'listener' to give user feedback on closed generation service when all loading states are false
    useEffect(() => {
        if (!loadingScope && !loadingAssumptions && !loadingLawyers && !loadingExperiences) {
            dispatchUserFeedback({ type: USERFEEDBACKACTIONS.SET_FIELD, field: "generating", value: false });
        }
    }, [loadingScope, loadingAssumptions, loadingLawyers, loadingExperiences, dispatchUserFeedback]);

    const withService = useCallback(async <T>(fn: (svc: GenerationService) => Promise<T>) => {
        const token = await getAccessTokenSilently();
        const svc = GenerationService.getInstance(token);
        return fn(svc);
    }, [getAccessTokenSilently]);

    const generateAll = useCallback(async (config: GenerationConfig) => {
        await withService(async (svc) => {
            dispatchUserFeedback({
                type: USERFEEDBACKACTIONS.SET_FIELD,
                field: 'generating',
                value: true,
            });

            try {
                await Promise.all([
                    svc.generateScope(config, handlers),
                    svc.generateAssumptions(config, handlers),
                    svc.generateLawyers(config, handlers),
                    svc.generateExperiences(config, handlers),
                ]);
            } catch (error) {
                console.error('Error during generation:', error);
            }
        });
    }, [withService, handlers, dispatchUserFeedback]);

    const cancelField = useCallback((field: Field) => {
        withService(async (svc) => {
            svc.cancelField(field);
        });
    }, [withService]);

    const cancelAll = useCallback(() => {
        withService(async (svc) => {
            svc.cancelAll();
            dispatchUserFeedback({
                type: USERFEEDBACKACTIONS.SET_FIELD,
                field: 'generating',
                value: false,
            });
        });
    }, [withService, dispatchUserFeedback]);

    return {
        generateAll,
        cancelField,
        cancelAll,
        withService,
    };
} 
